import {signalStore, withState, withMethods, patchState, withComputed, withHooks} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {computed, inject} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {pipe, switchMap, tap, catchError, of, debounceTime, forkJoin, filter, Observable} from 'rxjs';
import {ResultsStore} from '../../shared/store/results.store';
import {IAddonForItem, IAddonItem, IFeeItem} from '../../types/IAddonForItem';
import {AppStore} from '../../shared/store/app.store';
import {CartSnapshotService} from '../../shared/services/cart-snapshot.service';
import {ICartSnapshot} from '../../types/ICartSnapshot';
import {ItemsService} from '../../shared/services/items.service';
import {weightedPrice} from '../../shared/utils/item.utils';
import {setError, setFulfilled, setPending, withRequestStatus} from '../../shared/store/features/with-request-status';

function compareAddonKey(
  itemId: number | string,
  priceLevelId: number | string,
  price: number,
  relevantExcluded: (number | string)[],
): string {
  const base = `${itemId}_${priceLevelId}_${price}`;
  return relevantExcluded.length ? `${base}_${relevantExcluded.join('_')}` : base;
}

type CompareState = {
  selected_addon_ids: Set<number | string>;
  compare_addons_data: Record<string, IAddonForItem>;
  snapshot: any | null;
};

export const CompareStore = signalStore(
  withState<CompareState>({
    selected_addon_ids: new Set<number>(),
    compare_addons_data: {},
    snapshot: null,
  }),
  withRequestStatus('compare_addons'),
  withComputed((state, resultsStore = inject(ResultsStore)) => ({
    unified_addons: computed(() => {
      const compareItems = resultsStore.compare_items();
      const addonsData = resultsStore.addons_data();

      const itemIds = ([compareItems.good, compareItems.better, compareItems.best] as Array<{id: number} | undefined>)
        .filter((item): item is {id: number} => !!item)
        .map(item => String(item.id));

      const addonMap = new Map<number | string, IAddonItem>();
      const feeMap = new Map<number, IFeeItem>();

      Object.entries(addonsData).forEach(([key, addonForItem]) => {
        const matchesItem = itemIds.some(id => key.startsWith(id + '_'));
        if (!matchesItem) return;

        (addonForItem.addons ?? []).forEach(addon => {
          if (!addonMap.has(addon.item_id)) {
            addonMap.set(addon.item_id, addon);
          }
        });

        (addonForItem.fees ?? []).forEach(fee => {
          if (!feeMap.has(fee.fee_id)) {
            feeMap.set(fee.fee_id, fee);
          }
        });
      });

      return {
        addons: Array.from(addonMap.values()),
        fees: Array.from(feeMap.values()),
      };
    }),
  })),
  withComputed((store) => ({
    excluded_addon_ids_sorted: computed(() => {
      const all = store.unified_addons().addons.map(a => a.item_id);
      const selected = store.selected_addon_ids();
      return all.filter((id): id is number | string => !selected.has(id)).sort();
    }),
  })),
  withComputed((store, resultsStore = inject(ResultsStore)) => ({
    getCompareAddonsForItem: computed(() => (itemId: number | string, price: number, quantity: number): IAddonForItem | null => {
      const priceLevel = resultsStore.currentPriceLevel();
      if (!priceLevel) return null;
      const rawAddon = resultsStore.getAddonsForItem()(itemId, price, quantity);
      if (!rawAddon) return null;
      const itemAddonIds = new Set(rawAddon.addons?.map(a => a.item_id) ?? []);
      const excluded = store.excluded_addon_ids_sorted();
      const relevantExcluded = excluded.filter((id): id is number | string => itemAddonIds.has(id));
      const key = compareAddonKey(itemId, priceLevel.id, price, relevantExcluded);
      return store.compare_addons_data()[key] ?? null;
    }),
  })),
  withMethods((store, resultsStore = inject(ResultsStore), itemService = inject(ItemsService), appStore = inject(AppStore), cartSnapshotService = inject(CartSnapshotService)) => ({
    initSelectedIds() {
      const {addons} = store.unified_addons();
      const addon_ids = new Set(addons.map(a => a.item_id));

      const snapshot = appStore.cart().snapshot;
      if (snapshot) {
        addon_ids.clear();

        for (const grade of ['good', 'better', 'best'] as const) {
          const addons = snapshot?.snapshot?.[grade]?.['addons'] ?? null;
          if (!addons) continue;
          addons.addons.forEach((addon) => addon_ids.add(addon.item_id));
        }
      }

      patchState(store, {selected_addon_ids: addon_ids});
    },

    fetchCompareAddons: rxMethod<void>(
      pipe(
        debounceTime(300),
        tap(() => patchState(store, setPending('compare_addons'))),
        switchMap(() => {
          const compareItems = resultsStore.compare_items();
          const priceLevel = resultsStore.currentPriceLevel();
          if (!priceLevel) {
            patchState(store, setFulfilled('compare_addons'));
            return of({});
          }

          const excluded = store.excluded_addon_ids_sorted();
          const requestMap: Record<string, ReturnType<typeof itemService.getAddonsForItem>> = {};

          for (const grade of ['good', 'better', 'best'] as const) {
            const item = compareItems[grade];
            if (!item) continue;

            const price = weightedPrice(item);
            const rawAddon = resultsStore.getAddonsForItem()(item.id, price, item.sourcing.reduce((sum, s) => sum + s.selected_quantity, 0));
            if (!rawAddon) continue;

            const itemAddonIds = new Set(rawAddon.addons?.map(a => a.item_id) ?? []);
            const relevantExcluded = excluded.filter((id): id is number | string => itemAddonIds.has(id));
            const key = compareAddonKey(item.id, priceLevel.id, price, relevantExcluded);

            if (!relevantExcluded.length) {
              patchState(store, {compare_addons_data: {...store.compare_addons_data(), ...{[key]: rawAddon}}});
              continue;
            }

            if (key in store.compare_addons_data()) continue;

            const qty = (item.sourcing ?? []).reduce((sum, s) => sum + (s.selected_quantity || 0), 0);
            requestMap[key] = itemService.getAddonsForItem({
              item_id: item.id,
              brand: item.brand,
              model: item.model,
              item_number: item.item_number,
              price,
              quantity: qty,
              price_level_id: String(priceLevel.id),
              excluded_addon_ids: relevantExcluded,
            });
          }

          if (Object.keys(requestMap).length === 0) {
            patchState(store, setFulfilled('compare_addons'));
            return of({});
          }

          return forkJoin(requestMap).pipe(
            tap(results => {
              patchState(store, setFulfilled('compare_addons'), {
                compare_addons_data: {...store.compare_addons_data(), ...results},
              });
            }),
            catchError(error => {
              patchState(store, setError('compare_addons', error.message));
              return of({});
            }),
          );
        }),
      )
    ),

    hydrate() {
      const snapshot = appStore.cart().snapshot;
      if (snapshot) {
        for (const grade of ['good', 'better', 'best'] as const) {
          const item = snapshot?.snapshot?.[grade]?.['item'] ?? null;
          if (!item) continue;
          resultsStore.addCompareItem(item, grade);
        }
      }
    },

    // Pass an itemId to save only that compare item; otherwise every grade is saved
    createSnapshot(itemId?: number): Observable<{token: string}> {
      const compareItems = resultsStore.compare_items();
      const snapshot: ICartSnapshot = {};

      for (const grade of ['good', 'better', 'best'] as const) {
        const item = compareItems[grade];
        if (!item) continue;
        if (itemId !== undefined && item.id !== itemId) continue;

        const price = weightedPrice(item);
        const addon = store.getCompareAddonsForItem()(item.id, price, item.sourcing.reduce((sum, s) => sum + s.selected_quantity, 0));
        snapshot[grade] = {item, addons: addon};
      }

      patchState(store, {snapshot: snapshot});
      return cartSnapshotService.create(appStore.client_id(), appStore.cart.id(), snapshot);
    },
  })),
  withMethods((store) => ({
    toggleAddon(addonId: number | string) {
      const current = new Set(store.selected_addon_ids());
      if (current.has(addonId)) {
        current.delete(addonId);
      } else {
        current.add(addonId);
      }
      patchState(store, {selected_addon_ids: current});
      store.fetchCompareAddons();
    },
  })),
  withHooks({
    onInit(store) {
      const resultsStore = inject(ResultsStore);
      toObservable(resultsStore.addons_data).pipe(
        filter(data => Object.keys(data).length > 0),
      ).subscribe(() => {
        store.initSelectedIds();
        store.fetchCompareAddons();
      });
    },
  })
);
